/**
 * Metadata-specific atom watches for debugging the bug where
 * $metadataStateValue container.metadata becomes null during slow connections.
 */
import { Logger } from "@moment/logging";

import { $metadataStateValue } from "../document-metadata/metadata-state.atom";
import { watchAtom } from "./atom-tracer";
import type { Unwatch } from "./atom-tracer.types";

const logger = new Logger("metadata-watches");

const activeWatches = new Map<string, Unwatch>();

/**
 * Start watching a specific metadata state instance for unexpected clearing.
 *
 * Logs transitions where:
 * - metadata goes from non-null to null (the clearing bug)
 * - the entire container is set to null (unload)
 * - the container is first initialized (load)
 *
 * @param key - The metadata key (same key used with $metadataStateValue atomFamily)
 * @returns An unsubscribe function
 */
export function watchMetadataState(key: string): Unwatch {
	if (activeWatches.has(key)) {
		logger.warn("Already watching metadata state for key, replacing", { key });
		activeWatches.get(key)!();
	}

	const atom = $metadataStateValue(key);

	const unwatch = watchAtom(atom, {
		label: `metadataStateValue::${key}`,
		level: "info",
		maxSerializationDepth: 2,

		shouldLog: (prev, next) => {
			const prevHadMetadata = prev?.metadata != null;
			const nextHasMetadata = next?.metadata != null;

			// metadata cleared (the bug we're investigating)
			if (prevHadMetadata && !nextHasMetadata) {
				return true;
			}

			// entire container set to null (unload)
			if (prev != null && next == null) {
				return true;
			}

			// container first initialized (load)
			if (prev == null && next != null) {
				return true;
			}

			return false;
		},

		serialize: (value) => {
			if (value == null) {
				return null;
			}
			return {
				hasMetadata: value.metadata != null,
				metadataTitle: value.metadata?.title ?? null,
				metadataId: value.metadata?.id ?? null,
				metadataPageCount: value.metadata?.pagesMetadata?.length ?? 0,
				hasError: value.error != null,
				errorTag: (value.error as { tag?: string } | null)?.tag ?? null,
				errorMessage: value.error?.message ?? null,
				paramsKey: value.params?.key ?? null,
				hasOnPersist: value.params?.onPersist != null,
				hasSendToCollab: value.params?.sendToCollab != null,
				hasRemoteChanges: value.params?.remoteChanges != null,
				hasAbortController: value.abortController != null,
				abortControllerAborted: value.abortController?.signal?.aborted ?? null,
				lastWriteSource: value.lastWriteSource ?? null,
			};
		},
	});

	activeWatches.set(key, unwatch);

	return () => {
		unwatch();
		activeWatches.delete(key);
	};
}

/**
 * Stop watching a specific metadata state instance.
 */
export function unwatchMetadataState(key: string): void {
	const unwatch = activeWatches.get(key);
	if (unwatch) {
		unwatch();
		activeWatches.delete(key);
	}
}

/**
 * Stop all active metadata watches.
 */
export function unwatchAllMetadataStates(): void {
	for (const [key, unwatch] of activeWatches) {
		logger.info("Stopping metadata watch", { key });
		unwatch();
	}
	activeWatches.clear();
}
